import { UserProfile, UserHighScore, UserSession } from '../types/user.types';
import { ApiAuth } from './ApiAuth';

export class UserManager {
    private static instance: UserManager;
    private session: UserSession;
    
    private constructor() {
        this.session = this.loadSession();
    }
    
    public static getInstance(): UserManager {
        if (!UserManager.instance) {
            UserManager.instance = new UserManager();
        }
        return UserManager.instance;
    }
    
    private loadSession(): UserSession {
        const saved = localStorage.getItem('userSession');
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (error) {
                console.warn('Failed to parse user session, creating new one');
            }
        }
        
        return {
            currentUser: null,
            users: [],
            highScores: []
        };
    }
    
    private saveSession(): void {
        localStorage.setItem('userSession', JSON.stringify(this.session));
    }
    
    public getCurrentUser(): UserProfile | null {
        return this.session.currentUser;
    }

    public setAuthenticatedUser(user: UserProfile): void {
        this.session.currentUser = user;
        this.saveSession();
    }
    
    public getAllUsers(): UserProfile[] {
        return this.session.users;
    }
    
    public createUser(name: string, displayName?: string): UserProfile {
        const id = this.generateUserId();
        const now = new Date().toISOString();
        
        const newUser: UserProfile = {
            id,
            name: name.trim(),
            displayName: displayName?.trim() || name.trim(),
            createdAt: now,
            lastLogin: now,
            preferences: {
                soundEnabled: true,
                autoPlay: false
            },
            stats: {
                gamesPlayed: 0,
                totalWinnings: 0,
                totalLosses: 0,
                biggestWin: 0,
                favoriteGame: 'videopoker'
            }
        };
        
        this.session.users.push(newUser);
        this.saveSession();
        return newUser;
    }
    
    public loginUser(userId: string): boolean {
        const user = this.session.users.find(u => u.id === userId);
        if (user) {
            user.lastLogin = new Date().toISOString();
            this.session.currentUser = user;
            this.saveSession();
            return true;
        }
        return false;
    }
    
    public logoutUser(): void {
        this.session.currentUser = null;
        this.saveSession();
    }
    
    public updateUserStats(userId: string, stats: Partial<UserProfile['stats']>): void {
        const user = this.session.users.find(u => u.id === userId);
        if (user) {
            user.stats = { ...user.stats, ...stats };
            this.saveSession();
        }

        // Update current user if it's the same user
        if (this.session.currentUser?.id === userId) {
            this.session.currentUser.stats = { ...this.session.currentUser.stats, ...stats };
        }
    }

    public async updateBalance(newBalance: number, won: number = 0, lost: number = 0): Promise<void> {
        const currentUser = this.getCurrentUser();

        if (!currentUser || !currentUser.isAuthenticated) {
            return;
        }

        // Update local balance
        currentUser.balance = newBalance;

        // Sync with API server
        const apiAuth = ApiAuth.getInstance();
        await apiAuth.updateBalance(newBalance, won, lost);

        // Save session to localStorage
        this.saveSession();
    }
    
    public updateUserPreferences(userId: string, preferences: Partial<UserProfile['preferences']>): void {
        const user = this.session.users.find(u => u.id === userId);
        if (user) {
            user.preferences = { ...user.preferences, ...preferences };
            this.saveSession();
        }
    }
    
    public addHighScore(score: UserHighScore): void {
        this.session.highScores.push(score);
        // Keep only top 50 scores per game type
        this.session.highScores = this.session.highScores
            .sort((a, b) => b.score - a.score)
            .slice(0, 50);
        this.saveSession();
    }
    
    public getHighScores(game?: 'slots' | 'videopoker', variant?: string, limit: number = 10): UserHighScore[] {
        let scores = this.session.highScores;
        
        if (game) {
            scores = scores.filter(s => s.game === game);
        }
        
        if (variant) {
            scores = scores.filter(s => s.variant === variant);
        }
        
        return scores
            .sort((a, b) => b.score - a.score)
            .slice(0, limit);
    }
    
    public getUserHighScores(userId: string, limit: number = 10): UserHighScore[] {
        return this.session.highScores
            .filter(s => s.userId === userId)
            .sort((a, b) => b.score - a.score)
            .slice(0, limit);
    }
    
    public deleteUser(userId: string): boolean {
        const index = this.session.users.findIndex(u => u.id === userId);
        if (index >= 0) {
            this.session.users.splice(index, 1);
            // Remove user's high scores
            this.session.highScores = this.session.highScores.filter(s => s.userId !== userId);
            // Logout if current user
            if (this.session.currentUser?.id === userId) {
                this.session.currentUser = null;
            }
            this.saveSession();
            return true;
        }
        return false;
    }
    
    private generateUserId(): string {
        return 'user_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 5);
    }
    
    // Migration helper for existing localStorage data
    public migrateOldHighScores(): void {
        const oldScores = localStorage.getItem('highScores');
        const oldPlayerName = localStorage.getItem('playerName');
        
        if (oldScores && !this.session.highScores.length) {
            try {
                const scores = JSON.parse(oldScores);
                const migrationUser = this.createUser(oldPlayerName || 'Legacy Player');
                
                scores.forEach((score: any) => {
                    this.addHighScore({
                        userId: migrationUser.id,
                        userName: score.name || migrationUser.displayName,
                        score: score.score || 0,
                        game: 'videopoker', // Assume old scores are from video poker
                        date: score.date || new Date().toISOString()
                    });
                });
                
                console.log('Migrated old high scores to new user system');
            } catch (error) {
                console.warn('Failed to migrate old high scores:', error);
            }
        }
    }
}